import type { AgentClient, AgentScope, OperatorRole } from './api';
import { AGENT_SCOPES, scopeLevel, scopesUpToLevel } from './agents';

/**
 * Regras puras do operador no Claude (change platform-mcp-operator-access, PR 8.3 e OA-D12): quem é
 * cliente pessoal, que teto cabe no papel, os destaques do cartão e qual rota de consentimento um
 * pedido usa. A API revalida tudo (papel no banco, teto ⊆ papel ∩ `AllowedScopes`, só leitura,
 * titular, caminho ligado); aqui só se decide o que a tela mostra e o que ela deixa enviar.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Concessão pessoal que vence em menos disso ganha destaque (aviso D-3 do OA-D4). */
export const OPERATOR_EXPIRY_WARNING_DAYS = 3;
/** Recertificação: o mesmo D-30 do perfil do Hermes. */
export const OPERATOR_RECERTIFICATION_WARNING_DAYS = 30;

export const OPERATOR_INSTRUCTION =
  'Para conectar: o titular segue "Conectar ao Claude" no próprio perfil (Claude Code ou claude.ai) e aprova em /mcp/autorizar com o código do autenticador.';

/**
 * Login nativo do operador (change platform-mcp-operator-access, OA-D12): o Claude Code e o claude.ai
 * descobrem o servidor de autorização sozinhos pela URL do MCP do operador (RFC 9728 e RFC 8414),
 * com o `client_id` pré-registrado. A tela só monta os textos de instrução; o host vem da tag do
 * ambiente, como em `PlatformMcpAuthorizationServerUris` da API (nenhum host em configuração).
 */
export const OPERATOR_MCP_PATH = '/api/v1/platform/mcp-op';

/** A porta fixa da volta do Claude Code (`--callback-port`), a mesma do runbook. */
export const CLAUDE_CODE_CALLBACK_PORT = 53682;

/** A origem da API de cada tag, espelho de `PlatformMcpAuthorizationServerUris` (local: a do Docker). */
const API_ORIGIN_BY_TAG: Readonly<Record<OperatorEnvironmentTag, string>> = {
  local: 'http://host.docker.internal:5190',
  stg: 'https://api.staging.vaptmenu.com.br',
  prd: 'https://api.vaptmenu.com.br',
};

export type OperatorEnvironmentTag = 'local' | 'stg' | 'prd';

/** A tag do ambiente embutida no `client_id` de operador (`op-<tag>-<32 hex>`), ou nulo fora do formato. */
export function operatorEnvironmentTag(clientId: string | null | undefined): OperatorEnvironmentTag | null {
  if (!isOperatorClientId(clientId)) return null;
  return clientId!.split('-')[1] as OperatorEnvironmentTag;
}

/** A URL do MCP do operador na tag (o recurso canônico, sem barra final). */
export function operatorMcpUrl(tag: OperatorEnvironmentTag): string {
  return `${API_ORIGIN_BY_TAG[tag]}${OPERATOR_MCP_PATH}`;
}

/** O nome do servidor no Claude Code: um por ambiente, para staging e produção não se confundirem. */
export function claudeCodeServerName(tag: OperatorEnvironmentTag): string {
  return `vapt-backoffice-${tag}`;
}

/** `claude mcp add` em escopo de usuário, com o `client_id` pré-registrado e a porta fixa da volta. */
export function claudeCodeAddCommand(clientId: string, tag: OperatorEnvironmentTag): string {
  return `claude mcp add --transport http --scope user --client-id ${clientId} --callback-port ${CLAUDE_CODE_CALLBACK_PORT} ${claudeCodeServerName(tag)} ${operatorMcpUrl(tag)}`;
}

/** O login do servidor no Claude Code (o mesmo que `/mcp` → Authenticate). */
export function claudeCodeLoginCommand(tag: OperatorEnvironmentTag): string {
  return `claude mcp login ${claudeCodeServerName(tag)}`;
}

/**
 * Espelho do mapa papel → escopos MCP da API (`PlatformMcpOperatorScopes`, OA-D2, decisão O3),
 * documentado em `docs/api-specification.md`. A API não o expõe por rota, então a tela o copia
 * só para não oferecer teto que a API recusaria, cruzado com o `allowedScopes` do ambiente que
 * `GET /platform/me/operator-access` devolve (`roleScopesInEnvironment`; em produção, só
 * `metrics`). Quem decide é a API, que responde `422 scope_above_role` ou
 * `scope_not_allowed_in_environment`.
 */
export const OPERATOR_SCOPES_BY_ROLE: Readonly<Record<OperatorRole, readonly AgentScope[]>> = {
  SupportL1: ['platform.mcp.metrics.read', 'platform.mcp.stores.read'],
  SupportL2: ['platform.mcp.metrics.read', 'platform.mcp.stores.read'],
  Commercial: ['platform.mcp.metrics.read', 'platform.mcp.stores.read', 'platform.mcp.billing.read'],
  Admin: ['platform.mcp.metrics.read', 'platform.mcp.stores.read', 'platform.mcp.billing.read'],
};

/** Cliente pessoal de operador? Ausente (API anterior) vale como perfil do Hermes. */
export function isOperatorClient(client: Pick<AgentClient, 'clientKind'>): boolean {
  return client.clientKind === 'Operator';
}

/** Cliente de perfil do Hermes (o que a lista de perfis mostra): ausente ou `AgentProfile`. */
export function isProfileClient(client: Pick<AgentClient, 'clientKind'>): boolean {
  return client.clientKind === undefined || client.clientKind === null || client.clientKind === 'AgentProfile';
}

/** Os escopos que o papel pode receber, em ordem de nível. Papel desconhecido ou ausente: nenhum. */
export function roleScopes(role: string | null | undefined): AgentScope[] {
  if (!role || !Object.hasOwn(OPERATOR_SCOPES_BY_ROLE, role)) return [];
  const scopes = OPERATOR_SCOPES_BY_ROLE[role as OperatorRole];
  return AGENT_SCOPES.filter((scope) => scopes.includes(scope));
}

/**
 * Os escopos do papel que o ambiente libera: o papel ∩ `allowedScopes` de
 * `GET /platform/me/operator-access` (em produção, só métricas). Sem a leitura do ambiente (API
 * anterior à rota), `null` deixa só o papel, e a API recusa o resto com `422`.
 */
export function roleScopesInEnvironment(
  role: string | null | undefined,
  allowedScopes: readonly AgentScope[] | null,
): AgentScope[] {
  const scopes = roleScopes(role);
  return allowedScopes ? scopes.filter((scope) => allowedScopes.includes(scope)) : scopes;
}

/**
 * Os níveis de teto que cabem no papel: cada nível é o conjunto cumulativo, e só entra se todos os
 * escopos dele estão no papel. Vazio quando o papel não recebe nada.
 */
export function ceilingLevelsForRole(scopes: readonly AgentScope[]): number[] {
  const allowed = new Set<string>(scopes);
  const levels: number[] = [];
  for (let level = 1; level <= AGENT_SCOPES.length; level++) {
    if (scopesUpToLevel(level).every((scope) => allowed.has(scope))) levels.push(level);
  }
  return levels;
}

/** Os níveis para mudar o teto de um cliente pessoal: os do papel, menos o atual. */
export function ceilingChangeLevels(client: Pick<AgentClient, 'maxScopes'>, scopes: readonly AgentScope[]): number[] {
  const current = scopeLevel(client.maxScopes);
  return ceilingLevelsForRole(scopes).filter((level) => level !== current);
}

export type OperatorHighlightKind = 'expiring' | 'recertification_due';

export interface OperatorHighlight {
  kind: OperatorHighlightKind;
  label: string;
}

/**
 * Destaques do cliente pessoal. "Sem concessão" não é destaque: o operador conecta quando
 * precisa.
 */
export function operatorHighlights(
  client: Pick<AgentClient, 'status' | 'certificationDueAt' | 'activeGrant'>,
  now: Date,
): OperatorHighlight[] {
  const highlights: OperatorHighlight[] = [];
  const grant = client.activeGrant;
  if (grant) {
    const untilExpiry = new Date(grant.expiresAt).getTime() - now.getTime();
    if (untilExpiry < OPERATOR_EXPIRY_WARNING_DAYS * DAY_MS) {
      const days = Math.max(0, Math.floor(untilExpiry / DAY_MS));
      highlights.push({
        kind: 'expiring',
        label: days === 0 ? 'concessão vence hoje' : `concessão vence em ${days} ${days === 1 ? 'dia' : 'dias'}`,
      });
    }
  }
  if (client.status === 'Enabled') {
    const untilDue = new Date(client.certificationDueAt).getTime() - now.getTime();
    if (untilDue < 0) {
      highlights.push({ kind: 'recertification_due', label: 'recertificação vencida' });
    } else if (untilDue < OPERATOR_RECERTIFICATION_WARNING_DAYS * DAY_MS) {
      highlights.push({ kind: 'recertification_due', label: 'recertificação próxima' });
    }
  }
  return highlights;
}

/** O `client_id` de um cliente `Operator` (`PlatformAgentClientRules`, `\Aop-(local|stg|prd)-[0-9a-f]{32}\z`). */
const OPERATOR_CLIENT_ID = /^op-(?:local|stg|prd)-[0-9a-f]{32}$/;

export function isOperatorClientId(clientId: string | null | undefined): boolean {
  return typeof clientId === 'string' && OPERATOR_CLIENT_ID.test(clientId);
}

/**
 * O pedido guardado é do Claude Code pessoal? Decide a rota de consentimento (`operator` ou
 * `agent`) e o texto da volta, antes de qualquer POST: a resposta da API não diz o tipo no erro
 * depois da `redirect_uri`. Só um `client_id`, e de operador (o Claude Code e o conector do claude.ai
 * mandam o mesmo `op-<tag>-<32 hex>` pré-registrado, OA-D12); repetido, ausente ou em forma de URL
 * (CIMD) não é pessoal, e a API recusa do mesmo jeito. A query nunca vai ao DOM.
 */
export function isPersonalAuthorizationQuery(query: string | null | undefined): boolean {
  if (!query) return false;
  let ids: string[];
  try {
    ids = new URLSearchParams(query).getAll('client_id');
  } catch {
    return false;
  }
  return ids.length === 1 && isOperatorClientId(ids[0]);
}
